import ZipCodeFieldInput from '@buy-viasat/react/src/components/ZipCodeFieldInput';
import { Country, CustomerType, selectCustomerType } from '@buy-viasat/redux/src/app';
import { serviceabilityActions } from '@buy-viasat/redux/src/serviceability';
import { streetRegex, zipCodeRegex } from '@buy-viasat/utils';
import { Col, Container, Row, Txt } from '@vst/beam';
import { FormEventHandler, useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import AddressFieldInput from '../AddressFieldInput';
import AptUnitFieldInput from '../AptUnitFieldInput';
import styles from './multiFieldAddress.module.scss';

interface MultiFieldAddressFormProps {
  onApartmentUnitProvided: () => void;
  isApartmentUnitRequirementMet: boolean;
}

const MultiFieldAddressForm = ({
  onApartmentUnitProvided,
  isApartmentUnitRequirementMet,
}: MultiFieldAddressFormProps) => {
  const dispatch = useDispatch();
  const customerType = useSelector(selectCustomerType);
  const { t } = useTranslation(['serviceability']);

  const handleOnAddressChange = useCallback<FormEventHandler<HTMLInputElement>>(
    (e) => {
      dispatch(serviceabilityActions.setServiceAddressLine1(e.currentTarget.value));
      // Editing the street address invalidates the previous scrub's MDU determination, so the
      // alert re-evaluates (and can re-show) after the next scrub instead of staying dismissed.
      dispatch(serviceabilityActions.setHasFiberMduAddress(false));
    },
    [dispatch],
  );

  const handleOnAptUnitChange = useCallback<FormEventHandler<HTMLInputElement>>(
    (e) => {
      const value = e.currentTarget.value;
      if (value.length > 0) {
        onApartmentUnitProvided();
      }
      dispatch(serviceabilityActions.setServiceAddressLine2(value));
    },
    [dispatch, onApartmentUnitProvided],
  );

  const handleOnZipCodeChange = useCallback<FormEventHandler<HTMLInputElement>>(
    (e) => {
      dispatch(serviceabilityActions.setServiceAddressPostalCode(e.currentTarget.value));
    },
    [dispatch],
  );

  const addressLabel = (
    <Trans>
      {customerType === CustomerType.B
        ? t('serviceability.field.unit/Suite.label')
        : t('serviceability.field.aptUnit.label')}
      {isApartmentUnitRequirementMet && (
        <Txt variant={'smallRegular'} color={'subtle'}>
          {t('serviceability.optionalLabel')}
        </Txt>
      )}
    </Trans>
  );

  return (
    <Container fluid data-test-id="multiFieldAddressForm" className={styles['root']}>
      <Row>
        <Col>
          <Row>
            <Col>
              <AddressFieldInput
                fluid
                mb="24px"
                name="streetAddress"
                data-testid="streetAddress"
                labelProps={{ labelText: t('serviceability.field.streetAddress.label'), showStar: false }}
                validation={{
                  required: t('serviceability.field.streetAddress.validationRequired'),
                  invalid: t('serviceability.field.streetAddress.validationFormat'),
                  regex: streetRegex,
                }}
                onChange={handleOnAddressChange}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={6}>
              <AptUnitFieldInput
                fluid
                mb="24px"
                name="aptAddress"
                labelProps={{
                  labelText: addressLabel as unknown as string,
                  showStar: false,
                }}
                error={!isApartmentUnitRequirementMet ? t('common.mduFieldError') : undefined}
                helperTextProps={
                  !isApartmentUnitRequirementMet
                    ? {
                        helpMessage: t('common.mduFieldError'),
                        state: 'error' as const,
                      }
                    : undefined
                }
                onChange={handleOnAptUnitChange}
              />
            </Col>
            <Col xs={6}>
              <ZipCodeFieldInput
                fluid
                mb="24px"
                name="postalCodeAddress"
                data-testid="zipCodeField"
                labelProps={{ labelText: t('serviceability.field.zipCode.label'), showStar: false }}
                validation={{
                  required: t('serviceability.field.zipCode.validationRequired'),
                  invalid: t('serviceability.field.zipCode.validationFormat'),
                  regex: zipCodeRegex[Country.US],
                }}
                onChange={handleOnZipCodeChange}
              />
            </Col>
          </Row>
        </Col>
      </Row>
    </Container>
  );
};

export default MultiFieldAddressForm;
